import type { KyInstance } from 'ky';

import { ensureError, formatString, isEmpty, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import type { Tag } from '#tags';

import { Uri } from '#constants/uri';

/**
 * Upserts (inserts or updates) tags for a specific mailing list.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingListId - The unique identifier of the mailing list to update tags for.
 * @param data - An array of Tag objects to be upserted for the mailing list.
 * @returns A promise that resolves to an OperationResult indicating the outcome of the operation.
 * @throws {Error} If the mailingListId is not defined or the data array is empty.
 * @throws {Error} If the HTTP request fails or another error occurs.
 */
export const upsertMailingListTags = async (instance: KyInstance, mailingListId: number, data: Tag[]) => {
  try {
    if (isNotDefined(mailingListId) || isEmpty(data)) {
      throw new Error('MailingListId and tags are required.');
    }

    return await instance
      .post<OperationResult>(formatString(Uri.mailingList.upsertTags, mailingListId.toString()), {
        json: data,
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
